import {Component, Input} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {Observable} from "rxjs";
import {WarehouseService} from "../../warehouse/warehouse.service";
import {toggleLoading} from "../../misc/loading.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {ItsmWarehouseModel} from "../../warehouse/itsm-warehouse.model";
import {SelectedWarehouse} from "../../warehouse/warehouse.model";
import {shippingDestinationValues} from "../../order/order-value-maps";
import {ShippingDestination} from "../../order/order-form.gql-model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "select-staging-warehouse-dialog.template.pug",
})
export class SelectStagingWarehouseDialogComponent extends DialogComponent<null, SelectedWarehouse> {
  loading = true;
  searchText: string = "";
  pageSize: number = DEFAULT_PAGE_LIMIT;
  currentPage: number = DEFAULT_PAGE;
  maxPages: number;
  totalCount: number = 0;
  warehouses: ItsmWarehouseModel[] = [];
  form: FormGroup;
  error: Error;
  @Input() selectedShippingDestination: ShippingDestination = "STAGING_WAREHOUSE";
  @Input() title: string = "Select Staging Warehouse";

  constructor(
    public dialogService: DialogService,
    public warehouseService: WarehouseService,
    private formBuilder: FormBuilder,
  ) {
    super(dialogService);
    this.buildFormGroup();
  }

  private buildFormGroup() {
    this.form = this.formBuilder.group({
      warehouseId: [null, [Validators.required]],
    });
  }

  get shippingDestinationValues() {
    return Object.entries(shippingDestinationValues);
  }

  async ready() {
    this.loadData();
  }

  static showModal(dialogService: DialogService, data: any): Observable<SelectedWarehouse> {
    return dialogService.addDialog(SelectStagingWarehouseDialogComponent, data, {backdropColor: "rgba(0, 0, 0, 0.5)"});
  }

  @toggleLoading()
  @handleErrors()
  async loadData() {
    const {
      pageLength,
      pageNumber,
      totalStagingWarehouseCount,
      warehouses,
    } = await this.warehouseService
      .getWarehousesFromItsm(
        `${this.currentPage}`,
        `${this.pageSize}`,
        this.searchText,
      );
    this.pageSize = pageLength;
    this.currentPage = pageNumber;
    this.totalCount = totalStagingWarehouseCount;
    this.warehouses = warehouses;
    this.maxPages = Math.ceil(totalStagingWarehouseCount / pageLength);
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.loadData();
  }

  @handleErrors()
  save() {
    if (this.selectedShippingDestination !== "STAGING_WAREHOUSE") {
      this.result = {
        shippingDestination: this.selectedShippingDestination,
        warehouse: null,
      };
    } else {
      if (this.form.invalid) {
        this.form.markAsTouched();
        return;
      }
      const selectedWarehouse = this.warehouses
        .find(warehouse => warehouse.stagingWarehouseId === this.form.value.warehouseId);
      if (!selectedWarehouse) throw new Error("Error trying to select the warehouse.");
      this.result = {
        shippingDestination: this.selectedShippingDestination,
        warehouse: {
          id: selectedWarehouse.stagingWarehouseId,
          name: selectedWarehouse.name,
          line1: selectedWarehouse.address.line1,
          city: selectedWarehouse.address.city,
          region: selectedWarehouse.address.region,
          country: selectedWarehouse.address.country,
          regionAbbreviation: selectedWarehouse.address.regionAbbreviation,
        },
      };
    }
    this.close();
  }
}
